import { useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';
import { Box, Typography, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { useTimeline } from '../TimelineContext';
import { useChart } from '../../../../components/chart';
import { getSensorType } from '../../../../services/localizationService';

export default function TimelineSensorChartWidget({ recordings = [], events = [], recordingMetadata = {}, widgetConfig, onUpdateWidget }) {
  const { currentTime, seek, window: timeWindow } = useTimeline();
  const selectedSensorType = widgetConfig?.selectedSensorType || '';

  // Collect all unique sensor types across all recordings
  const allSensorTypes = useMemo(() => {
    const types = new Set();
    recordings.forEach((rec) => {
      Object.keys(rec.data || {}).forEach((key) => {
        const sensorType = key.split(':').pop();
        if (!['altitude', 'latitude', 'longitude'].includes(sensorType.toLowerCase())) {
          types.add(sensorType);
        }
      });
    });
    return [...types].sort();
  }, [recordings]);

  // Auto-select first sensor type if none selected
  const activeSensorType = selectedSensorType || allSensorTypes[0] || '';

  // Build series: one per recording that has this sensor type
  const { series } = useMemo(() => {
    if (!activeSensorType) return { series: [], allLabels: [] };

    const resultSeries = [];
    const labelsSet = new Set();

    recordings.forEach((rec) => {
      const matchingKey = Object.keys(rec.data || {}).find(
        (k) => k.split(':').pop().toLowerCase() === activeSensorType.toLowerCase()
      );
      if (!matchingKey || !rec.data[matchingKey]) return;

      const meta = recordingMetadata[rec.id];
      const color = meta?.color || '#1976d2';
      const recName = meta?.label || rec.name || 'Unknown';
      const points = rec.data[matchingKey];

      const data = points.map((dp) => {
        labelsSet.add(dp.time);
        return [dp.time, dp.value];
      });

      resultSeries.push({
        name: recName,
        data,
        color,
      });
    });

    return { series: resultSeries, allLabels: [...labelsSet].sort((a, b) => a - b) };
  }, [recordings, activeSensorType, recordingMetadata]);

  // Collect all rule events across all recordings
  const eventAnnotations = useMemo(() => {
    if (!events || events.length === 0) return [];
    return events.map((evt) => ({
      x: new Date(evt.startTime).getTime(),
      x2: new Date(evt.endTime).getTime(),
      fillColor: '#d50100',
      label: { text: evt.label },
    }));
  }, [events]);

  const chartOptions = useChart({
    plotOptions: { bar: { columnWidth: '16%' } },
    fill: { type: ['solid'] },
    colors: series.map((s) => s.color),
    xaxis: {
      type: 'datetime',
      min: timeWindow?.start,
      max: timeWindow?.end,
      labels: { datetimeUTC: false },
    },
    annotations: {
      xaxis: [
        ...eventAnnotations,
        {
          x: currentTime,
          borderColor: '#775DD0',
          label: {
            borderColor: '#775DD0',
            style: { color: '#fff', background: '#775DD0' },
            text: 'Current Time',
          },
        },
      ],
    },
    chart: {
      toolbar: {
        show: true,
        tools: { download: true, selection: true, pan: true, zoom: true },
      },
      events: {
        markerClick: (_, chartContext, opts) => {
          const seriesIndex = opts.seriesIndex;
          const dataPointIndex = opts.dataPointIndex;
          if (series[seriesIndex]?.data?.[dataPointIndex]) {
            const timestamp = series[seriesIndex].data[dataPointIndex][0];
            seek(timestamp);
          }
        },
      },
    },
    stroke: { width: 2, curve: 'smooth' },
    legend: {
      show: true,
      position: 'top',
      horizontalAlign: 'left',
    },
    tooltip: {
      shared: false,
      custom: ({ series: seriesData, seriesIndex, dataPointIndex, w }) => {
        const value = seriesData[seriesIndex]?.[dataPointIndex] ?? 'N/A';
        const color = w.globals.colors[seriesIndex];
        const name = w.globals.seriesNames[seriesIndex];
        return `
          <div style="padding: 8px; border-radius: 9px;">
            <div style="display: flex; align-items: center;">
              <span style="background-color: ${color}; border-radius: 50%; width: 12px; height: 12px; margin-right: 8px;"></span>
              <span>${name}: <strong>${value}</strong></span>
            </div>
          </div>
        `;
      },
    },
  });

  if (recordings.length === 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <Typography color="text.secondary" variant="body2">No recordings in workspace</Typography>
      </Box>
    );
  }

  if (allSensorTypes.length === 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <Typography color="text.secondary" variant="body2">No sensor data available</Typography>
      </Box>
    );
  }

  // Convert series to ApexCharts format (array of [x, y])
  const apexSeries = series.map((s) => ({
    name: s.name,
    type: 'line',
    data: s.data,
  }));

  return (
    <Box sx={{ p: 1, pb: 0, width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }} dir="ltr">
      <FormControl size="small" sx={{ mb: 0.5, maxWidth: 220, flexShrink: 0 }}>
        <InputLabel>Sensor Type</InputLabel>
        <Select
          value={activeSensorType}
          label="Sensor Type"
          onChange={(e) => onUpdateWidget?.({ selectedSensorType: e.target.value })}
        >
          {allSensorTypes.map((t) => (
            <MenuItem key={t} value={t}>{getSensorType(t)}</MenuItem>
          ))}
        </Select>
      </FormControl>
      <Box sx={{ flex: 1, minHeight: 0 }}>
        {apexSeries.length > 0 ? (
          <ReactApexChart type="line" series={apexSeries} options={chartOptions} height="100%" />
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Typography color="text.secondary" variant="body2">
              No recordings contain {getSensorType(activeSensorType)} data
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
